import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Maximize2, Minimize2, Minus, Search, X } from 'lucide-react';
import { CommentHeader } from '../CommentHeader';
import {
  isCompactPanelSize,
  isDensePanelSize,
  LiveCommentsSizePreset,
} from './live-comments-panel-layout';
import { LiveCommentsSizePicker } from './live-comments-size-picker';

type LiveCommentsPanelHeaderProps = {
  title: string;
  commentsCount: number;
  isMobile: boolean;
  size: LiveCommentsSizePreset;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSizeChange: (size: LiveCommentsSizePreset) => void;
  onToggleSize: () => void;
  onMinimize: () => void;
  onClose: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
};

export function LiveCommentsPanelHeader({
  title,
  commentsCount,
  isMobile,
  size,
  searchQuery,
  onSearchChange,
  onSizeChange,
  onToggleSize,
  onMinimize,
  onClose,
  onRefresh,
  isRefreshing,
}: LiveCommentsPanelHeaderProps) {
  const compact = isCompactPanelSize(size);
  const dense = isDensePanelSize(size);
  const showSearch = size === 'md' || size === 'lg';

  return (
    <div
      className={cn(
        'sticky top-0 z-10 shrink-0 border-b bg-gradient-to-r from-blue-50 to-indigo-50/50 backdrop-blur-sm',
        dense
          ? 'space-y-1.5 px-1.5 py-1.5'
          : compact
            ? 'space-y-1.5 px-2 py-1.5'
            : 'space-y-2 px-2.5 py-2',
      )}
    >
      <div className="flex min-w-0 items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <CommentHeader
            title={title}
            commentsCount={commentsCount}
            onRefresh={onRefresh}
            isRefreshing={isRefreshing}
            compact={compact}
          />
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          <HeaderIconButton label="Minimize" onClick={onMinimize} dense={dense}>
            <Minus className="h-3.5 w-3.5 text-gray-600" />
          </HeaderIconButton>
          {!isMobile && (
            <HeaderIconButton label={size === 'lg' ? 'Restore' : 'Maximize'} onClick={onToggleSize} dense={dense}>
              {size === 'lg' ? (
                <Minimize2 className="h-3.5 w-3.5 text-gray-600" />
              ) : (
                <Maximize2 className="h-3.5 w-3.5 text-gray-600" />
              )}
            </HeaderIconButton>
          )}
          <HeaderIconButton label="Close" onClick={onClose} dense={dense} className="hover:bg-red-50">
            <X className="h-3.5 w-3.5 text-gray-600 hover:text-red-600" />
          </HeaderIconButton>
        </div>
      </div>

      <div className="flex min-w-0 items-center gap-1">
        <LiveCommentsSizePicker size={size} onChange={onSizeChange} compact={dense} />

        {showSearch && (
          <div className="relative min-w-0 flex-1">
            <Input
              value={searchQuery}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search..."
              className={cn(
                'h-6 border-gray-300 pl-6 pr-1 text-xs focus:border-blue-400 focus:ring-blue-400 sm:h-7',
              )}
            />
            <Search className="absolute left-1.5 top-1.5 h-3.5 w-3.5 text-gray-400" />
          </div>
        )}
      </div>
    </div>
  );
}

function HeaderIconButton({
  label,
  onClick,
  children,
  className,
  dense,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  className?: string;
  dense?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'group rounded-md transition-all hover:bg-white/80 active:bg-gray-100',
        dense ? 'p-0.5' : 'p-1',
        className,
      )}
    >
      {children}
    </button>
  );
}
